/**
 * DashboardHome — the PMS dashboard home CONTENT (greeting + widget grid).
 *
 * Extracted from the former `DashboardPage` (SCRUM-297 / MENU-1): that
 * component was a full shell (PageFrame + TopPill + LeftMenu + its own
 * `<main>`). The shell role now belongs to `PmsShellV4`; this component is
 * just the home content that renders inside the shell's `<main>` as the
 * `/` index route (`path: ''`). No shell chrome here — no double frame.
 *
 * Pulls its own live data (`useCurrentUser` / `useCurrentOrg` /
 * `useUserPreferences`) so it stays a self-contained route element, matching
 * the data-plumbing-owns-itself pattern the dashboard used before. The
 * render guard (`user === null` / `orgState === null` → render nothing) is
 * kept so a transient null state can't throw; in practice `PmsShellV4`
 * already gates on the same nulls one level up.
 *
 * Customize / Add-widget are no-ops (TODO(SCRUM-63)) carried over verbatim.
 */
import * as React from 'react';

import { useCurrentUser } from '../../shared/hooks/useCurrentUser.js';
import { useCurrentOrg } from '../../shared/hooks/useCurrentOrg.js';
import { useUserPreferences } from '../../shared/hooks/useUserPreferences.js';
import { useCompactMode } from '../../shared/hooks/useCompactMode.js';

import { GreetingHero } from './components/GreetingHero.js';
import { WidgetGrid } from './components/WidgetGrid.js';

export interface DashboardHomeProps {
  /**
   * Override compact mode (tests / SSR). When omitted, compact is
   * self-detected from the viewport via `useCompactMode`: the router mounts
   * `<DashboardHome />` with no prop, so the home content must follow the real
   * viewport — the same compact signal the shell chrome uses — rather than
   * silently rendering desktop paddings + desktop-only controls on a narrow
   * screen (the MENU-1 compact-home regression Codex flagged).
   */
  readonly compact?: boolean;
  /** Override the "now" Date (for tests / SSR). */
  readonly now?: Date;
}

export function DashboardHome({
  compact: compactOverride,
  now,
}: DashboardHomeProps = {}): React.ReactElement | null {
  const user = useCurrentUser();
  const orgState = useCurrentOrg();
  const { preferences } = useUserPreferences();
  const compact = useCompactMode(compactOverride);

  // Memoize the clock so toggling theme/density elsewhere doesn't construct a
  // fresh `new Date()` each render (carried over from DashboardPage).
  const clock = React.useMemo(() => now ?? new Date(), [now]);

  if (user === null) return null;
  if (orgState === null) return null;

  const membership = orgState.membership;

  // No-op handlers (TODO(SCRUM-63): open the widget library drawer).
  const onAddWidget = (): void => {
    // TODO(SCRUM-63): open widget library drawer.
  };
  const onCustomize = (): void => {
    // TODO(SCRUM-63): open widget library drawer in customize mode.
  };

  return (
    <div className={compact ? 'pt-4 px-4 pb-8' : 'pt-2 px-8 pb-10'}>
      <GreetingHero
        user={user}
        org={{ name: membership.organizationName }}
        now={clock}
        mode={preferences.density}
        compact={compact}
        onCustomize={onCustomize}
        onAddWidget={onAddWidget}
      />
      <WidgetGrid
        mode={preferences.density}
        compact={compact}
        onAddWidget={onAddWidget}
      />
    </div>
  );
}
